/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { filterType } from '../../config/enum';
import DefaultApiParameters from '../../enum/DefaultApiParameters';
import {
  DeviceTransactionProperties,
  DeviceTransactionRequest,
  DeviceTransactionResponse,
  DeviceTransactionInitialProperties,
} from '../../interface/device-transactions.interface';
import { CommonFilterProperties, FilterUpdateProperties } from '../../interface/filter.interface';
import {
  ClearFilters,
  ConfigureListFilterResponse,
  RemoveSingleFilterFromList,
  UpdateCommonFilter,
} from '../../shared-functions/CommonFilters';
import ConvertUTCtoDate from '../../shared-functions/ConvertUTCtoDate';
import ConvertUTCtoHHMM from '../../shared-functions/ConvertUTCtoHHMM';
import { removeEmptyKeys } from '../../shared-functions/DateTime';
import FormatUtcString from '../../shared-functions/FormatUtcString';
import cpoTransactionApi from '../../utils/api/cpo-transaction';

/**
 * Thunk to fetch device transactions using the transaction list API.
 * @param {DeviceTransactionRequest} data - The parameters for fetching transactions.
 * @param {any} thunkAPI - The thunk API object containing dispatch.
 * @returns {Promise<DeviceTransactionResponse>} Resolves with the device transaction response data.
 */
export const fetchDeviceTransaction = createAsyncThunk<
  DeviceTransactionResponse,
  DeviceTransactionRequest
>(
  'device-transaction/fetchDeviceTransaction',
  async (data: DeviceTransactionRequest, { dispatch }) => {
    try {
      const sn = data.chargingPointSn || data.chargingpointsn;
      const accountCustomIdValue = (data as any).accountCustomId;
      const station = (data as any).stationId || (data as any).chargingStation;

      const queryData: Record<string, any> = { ...data };
      delete queryData.chargingpointsn;
      delete queryData.chargingPointSn;
      delete queryData.accountCustomId;
      delete queryData.chargingStation;
      delete queryData.stationId;

      if (sn) {
        queryData.chargingPointSn = Array.isArray(sn) ? sn : [sn];
      }
      if (accountCustomIdValue) {
        queryData.accountCustomId = Array.isArray(accountCustomIdValue)
          ? accountCustomIdValue
          : [accountCustomIdValue];
      }
      if (station) {
        queryData.stationId = Array.isArray(station) ? station : [station];
      }

      const queryParameters = removeEmptyKeys(queryData);
      const response = await cpoTransactionApi.fetchCpoTransactions(queryParameters);
      return response.data;
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationMessage',
        payload: {
          type: 'error',
          CONNREFUSED: error?.response?.data?.code || 200,
          message: error?.response?.data?.message || 'Something went wrong!',
          status: true,
        },
      });
      throw error;
    }
  },
);

/**
 * Thunk to refresh device transactions using the transaction list API.
 * @param {DeviceTransactionRequest} data - The parameters for refreshing transactions.
 * @returns {Promise<DeviceTransactionResponse>} Resolves with the refreshed device transaction response data.
 */
export const refreshDeviceTransaction = createAsyncThunk<
  DeviceTransactionResponse,
  DeviceTransactionRequest
>(
  'device-transaction/refreshDeviceTransaction',
  async (data: DeviceTransactionRequest) => {
    const sn = data.chargingPointSn || data.chargingpointsn;
    const accountCustomIdValue = (data as any).accountCustomId;
    const station = (data as any).stationId || (data as any).chargingStation;

    const queryData: Record<string, any> = { ...data };
    delete queryData.chargingpointsn;
    delete queryData.chargingPointSn;
    delete queryData.accountCustomId;
    delete queryData.chargingStation;
    delete queryData.stationId;

    if (sn) {
      queryData.chargingPointSn = Array.isArray(sn) ? sn : [sn];
    }
    if (accountCustomIdValue) {
      queryData.accountCustomId = Array.isArray(accountCustomIdValue)
        ? accountCustomIdValue
        : [accountCustomIdValue];
    }
    if (station) {
      queryData.stationId = Array.isArray(station) ? station : [station];
    }

    const queryParameters = removeEmptyKeys(queryData);
    const response = await cpoTransactionApi.fetchCpoTransactions(queryParameters);
    return response.data;
  },
);

/**
 * Thunk to fetch transaction list filters.
 * @param {void} _ - Unused payload parameter.
 * @param {any} thunkAPI - The thunk API object containing dispatch.
 * @returns {Promise<CommonFilterProperties[]>} Resolves with array of filter properties.
 */
export const fetchListTransactionFilters = createAsyncThunk<
  CommonFilterProperties[], // Return type
  void // No need for an argument, since we are not using the payload
>('device-transaction/fetchListTransactionFilters', async (_, { dispatch }) => {
  try {
    const response = await cpoTransactionApi.fetchCpoTransactionFilters();
    const updatedResponse: CommonFilterProperties[] = ConfigureListFilterResponse(response.data);
    return updatedResponse;
  } catch (error: any) {
    dispatch({
      type: 'notification/setNotificationDetails',
      payload: {
        type: 'error',
        notificationDetails: {
          statusCode: error?.response?.data?.code || 200,
          message: error?.response?.data?.message || 'Something went wrong!',
        },
      },
    });
    throw error;
  }
});

const deviceFilter: CommonFilterProperties[] = [
  {
    name: 'Search here...',
    key: 'searchBy',
    type: filterType.TEXT,
    value: '',
    applyFilter: false,
    enable: true,
    searchBy: true,
  },
];

// Initial state
const initialState: DeviceTransactionInitialProperties = {
  transactions: [],
  limit: DefaultApiParameters.limit,
  page: DefaultApiParameters.page,
  totalPages: 0,
  totalResults: 0,
  loading: false,
  filters: deviceFilter,
  filterListLoading: false,
  sortBy: '',
  sortField: '',
};

/**
 * Redux slice for managing device transaction state.
 */
export const deviceTransactionSlice = createSlice({
  name: 'device-transaction',
  initialState,
  reducers: {
    /**
     * Update device management filters
     * @param {any} state The current slice state.
     * @param {any} action The action payload containing filter updates.
     */
    updateTransactionFilters: (state, action: PayloadAction<FilterUpdateProperties>) => {
      const payload = { ...action.payload };
      if (payload.type === 'add') {
        const { updatedCommonFilter } = UpdateCommonFilter(state.filters, action.payload);
        state.filters = updatedCommonFilter;
      } else if (payload.type === 'remove') {
        state.filters = RemoveSingleFilterFromList(state.filters, payload.key);
      } else if (payload.type === 'clear') {
        state.filters = ClearFilters(state.filters);
      }
    },
    /**
     * function for set sortBy
     * @param {any} state The current slice state.
     * @param {any} action The action payload containing sorting criteria.
     */
    setSortData: (state, action: PayloadAction<{ sortField: string; sortBy: 'asc' | 'desc' }>) => {
      state.sortField = action.payload.sortField;
      state.sortBy = action.payload.sortBy;
    },
  },
  /**
   * Use the extra reducers function
   * @param {any} builder The action reducer map builder.
   */
  extraReducers: (builder) => {
    builder
      .addCase(fetchDeviceTransaction.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchDeviceTransaction.fulfilled, (state, action) => {
        state.loading = false;
        state.transactions = action.payload?.results?.length
          ? action.payload.results?.map((stationData: DeviceTransactionProperties) => {
              const startDate =
                stationData.transactionStartsAt ||
                (stationData as any).startTime ||
                (stationData as any).createdAt;
              return {
                ...stationData,
                id: stationData._id,
                date: startDate ? ConvertUTCtoDate(startDate) : '',
                time: startDate ? FormatUtcString(ConvertUTCtoHHMM(startDate)) : '',
              };
            })
          : [];
        state.limit = action.payload.limit;
        state.page = action.payload.page;
        state.totalPages = action.payload.totalPages;
        state.totalResults = action.payload.totalResults;
      })
      .addCase(fetchDeviceTransaction.rejected, (state) => {
        state.loading = false;
        // Handle error if needed
      });
    builder.addCase(refreshDeviceTransaction.fulfilled, (state, action) => {
      state.transactions = action.payload?.results?.length
        ? action.payload.results?.map((stationData: DeviceTransactionProperties) => {
            const startDate =
              stationData.transactionStartsAt ||
              (stationData as any).startTime ||
              (stationData as any).createdAt;
            return {
              ...stationData,
              id: stationData._id,
              date: startDate ? ConvertUTCtoDate(startDate) : '',
              time: startDate ? FormatUtcString(ConvertUTCtoHHMM(startDate)) : '',
            };
          })
        : [];
      state.limit = action.payload.limit;
      state.page = action.payload.page;
      state.totalPages = action.payload.totalPages;
      state.totalResults = action.payload.totalResults;
    });
    builder
      .addCase(fetchListTransactionFilters.pending, (state) => {
        state.filterListLoading = true;
      })
      .addCase(
        fetchListTransactionFilters.fulfilled,
        (state, action: PayloadAction<CommonFilterProperties[]>) => {
          state.filterListLoading = false;
          state.filters = [...(state.filters || []), ...action.payload];
        },
      )
      .addCase(fetchListTransactionFilters.rejected, (state) => {
        state.filterListLoading = false;
      });
  },
});

/**
 * Legacy alias export for guestChargingSlice.
 */
export const guestChargingSlice = deviceTransactionSlice;

// Export the actions
export const { updateTransactionFilters, setSortData } = deviceTransactionSlice.actions;
// Export the reducer
export default deviceTransactionSlice.reducer;
